import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography
} from '@mui/material';
import DashboardCard from '../components/DashboardCard';
import {
    GoogleMap,
    Marker,
    InfoWindow
} from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { api } from 'common';
import { colors } from 'components/Theme/WebTheme';
import { FONT_FAMILY } from 'common/sharedFunctions';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);

    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    // Nouvelles statistiques
    const [pendingBookings, setPendingBookings] = useState(0);
    const [acceptedBookings, setAcceptedBookings] = useState(0);
    const [ongoingTrips, setOngoingTrips] = useState(0);
    const [todayCompletedBookings, setTodayCompletedBookings] = useState(0);
    const [todayCancelledBookings, setTodayCancelledBookings] = useState(0);
    const [weeklyGross, setWeeklyGross] = useState(0);
    const [avgTripValue, setAvgTripValue] = useState(0);
    const [fleetAdminCount, setFleetAdminCount] = useState(0);
    const dispatch = useDispatch();
    const {fetchDrivers, clearFetchDrivers} = api;

    function formatAmount(value, decimal, country) {
        const number = parseFloat(value || 0);
        if (country === "Vietnam") {
          return number.toLocaleString("vi-VN", {
            minimumFractionDigits: decimal,
            maximumFractionDigits: decimal
          });
        } else {
          return number.toLocaleString("en-US", {
            minimumFractionDigits: decimal,
            maximumFractionDigits: decimal
          });
        }
    }

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        return () => {
            dispatch(clearFetchDrivers());
        };
    }, [dispatch, fetchDrivers, clearFetchDrivers]);

    // Position par défaut : Lorient
    const defaultLocation = { lat: 47.7482, lng: -3.3702 };

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }),
                error => {
                    console.log(error);
                    // Utiliser la position par défaut si géolocalisation échoue
                    setMylocation(defaultLocation);
                }
            );
        }
    }, [mylocation]);

    // Centrer la carte sur les chauffeurs actifs s'il y en a
    useEffect(() => {
        if (locations && locations.length > 0) {
            // Calculer le centre des chauffeurs actifs
            const avgLat = locations.reduce((sum, loc) => sum + loc.lat, 0) / locations.length;
            const avgLng = locations.reduce((sum, loc) => sum + loc.lng, 0) / locations.length;
            setMylocation({ lat: avgLat, lng: avgLng });
        }
    }, [locations]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (usersdata.drivers && bookinglistdata.bookings) {
            const liveBookings = bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED');
            const drivers = usersdata.drivers;
            let locs = [];
           
                for (let i = 0; i < drivers.length; i++) {
                    // Afficher uniquement les chauffeurs actifs (connectés) avec une localisation
                    if (drivers[i].location && drivers[i].driverActiveStatus === true) {
                        let carImage = 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';
                        let bookingRef = null;
                        for (let j = 0; j < cars.length; j++) {
                            if (cars[j].name === drivers[i].carType) {
                                carImage = cars[j].image;
                            }
                        }
                        for(let j = 0; j<liveBookings.length; j++){
                            if(liveBookings[j].driver === drivers[i].uid){
                                bookingRef = liveBookings[j].reference;
                            }
                        }
                        locs.push({
                            id: i,
                            lat: drivers[i].location.lat,
                            lng: drivers[i].location.lng,
                            drivername: drivers[i].firstName + ' ' + drivers[i].lastName,
                            carnumber: drivers[i].vehicleNumber,
                            cartype: drivers[i].carType,
                            carImage: carImage,
                            bookingRef: bookingRef,
                            fleetadmin:drivers[i].fleetadmin
                        });
                    }
                }
                if (auth.profile.usertype === 'fleetadmin') {
                    locs = locs.filter(driver => driver.fleetadmin === auth.profile.uid);
                }
                setLocations(locs);
                
        }
    }, [usersdata.drivers, auth.profile.usertype, auth.profile.uid, cars, bookinglistdata.bookings, settings]);
    
    // Calcul des statistiques de réservations en temps réel
    useEffect(() => {
        if (bookinglistdata.bookings) {
            const today = new Date();
            let pending = 0;
            let accepted = 0;
            let ongoing = 0;
            let todayCompleted = 0;
            let todayCancelled = 0;
            let weeklyTotal = 0;
            let completedTripsTotal = 0;
            let completedTripsCount = 0;
            
            // Calcul de la date il y a 7 jours
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);

            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                const booking = bookinglistdata.bookings[i];
                const isRelevant = (auth.profile.usertype === 'fleetadmin' && booking.fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin';
                
                if (isRelevant) {
                    const tripDate = new Date(booking.tripdate);
                    const isToday = tripDate.getDate() === today.getDate() && 
                                   tripDate.getMonth() === today.getMonth() && 
                                   tripDate.getFullYear() === today.getFullYear();
                    const isThisWeek = tripDate >= weekAgo;

                    // Comptage par statut
                    if (booking.status === 'NEW' || booking.status === 'PENDING') {
                        pending++;
                    }
                    if (booking.status === 'ACCEPTED') {
                        accepted++;
                    }
                    if (booking.status === 'STARTED' || booking.status === 'ARRIVED' || booking.status === 'REACHED') {
                        ongoing++;
                    }
                    if ((booking.status === 'PAID' || booking.status === 'COMPLETE') && isToday) {
                        todayCompleted++;
                    }
                    if (booking.status === 'CANCELLED' && isToday) {
                        todayCancelled++;
                    }
                    
                    // Calcul revenus semaine
                    if ((booking.status === 'PAID' || booking.status === 'COMPLETE') && isThisWeek) {
                        const fees = auth.profile.usertype === 'admin' 
                            ? parseFloat(booking.convenience_fees || 0) - parseFloat(booking.discount || 0)
                            : parseFloat(booking.fleetCommission || 0);
                        weeklyTotal += fees;
                    }
                    
                    // Calcul valeur moyenne
                    if (booking.status === 'PAID' || booking.status === 'COMPLETE') {
                        completedTripsTotal += parseFloat(booking.trip_cost || 0);
                        completedTripsCount++;
                    }
                }
            }
            
            setPendingBookings(pending);
            setAcceptedBookings(accepted);
            setOngoingTrips(ongoing);
            setTodayCompletedBookings(todayCompleted);
            setTodayCancelledBookings(todayCancelled);
            setWeeklyGross(parseFloat(weeklyTotal).toFixed(settings.decimal || 2));
            setAvgTripValue(completedTripsCount > 0 ? parseFloat(completedTripsTotal / completedTripsCount).toFixed(settings.decimal || 2) : 0);
        }
    }, [bookinglistdata.bookings, auth.profile.uid, auth.profile.usertype, settings.decimal]);

    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if ((bookinglistdata.bookings[i].status === 'PAID' || bookinglistdata.bookings[i].status === 'COMPLETE') && ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin')|| auth.profile.usertype === 'admin') ) {
                    const { tripdate, convenience_fees, fleetCommission, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && auth.profile.usertype === 'admin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                    }
                    if (fleetCommission && parseFloat(fleetCommission) > 0 && auth.profile.usertype === 'fleetadmin'){
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(fleetCommission);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(fleetCommission);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(fleetCommission);
                    }
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
        }
    }, [bookinglistdata.bookings, settings,auth.profile.uid,auth.profile.usertype]);

    const barChartOptions = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        colors: [colors.GREEN, colors.RED],
        options: {
            maintainAspectRatio: false,
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                        },
                    },
                ],
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'smooth'
            }, 
        },
        chart: {
                height: 350,
                type: 'area',
                fontFamily: FONT_FAMILY 
            },
        xaxis: {
            labels: {
                style: {
                    fontFamily: FONT_FAMILY 
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    fontFamily: FONT_FAMILY 
                }
            }
        },
        title: {
            style: {
                fontFamily: FONT_FAMILY
            }
        },
        subtitle: {
            style: {
                fontFamily: FONT_FAMILY 
            }
        },
        tooltip: {
            style: {
                fontFamily: FONT_FAMILY 
            }
        },
        legend: {
            fontFamily: FONT_FAMILY 
        }
    };

    const barChart = [{
        name: t('completed_bookings'),
        data: allCompleteCount
    }, {
        name: t('cancelled_bookings'),
        data: allCancelCount
    }];
    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            let fleetCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === 'driver') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        driverCount = driverCount + 1;
                    }
                }
                if (usersdata.users[i].driverActiveStatus === true) {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        activeCount = activeCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === 'customer') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        customerCount = customerCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === 'fleetadmin' && auth.profile.usertype === 'admin') {
                    fleetCount = fleetCount + 1;
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
            setFleetAdminCount(fleetCount);
        } else {
            setActiveCount(0)
            setDriverCount(0)
            setCustomerCount(0)
            setFleetAdminCount(0)
        }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        let allCompleteCount = [];
        let allCancelCount = [];
        let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let completeCount = 0;
                let cancelCount = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const { tripdate } = bookinglistdata.bookings[j];
                    let tDate = new Date(tripdate);
                    if (months[i] === months[tDate.getMonth()] && (bookinglistdata.bookings[j].status === 'PAID' || bookinglistdata.bookings[j].status === 'COMPLETE')) {
                        completeCount = completeCount + 1;
                    }
                    if (months[i] === months[tDate.getMonth()] && bookinglistdata.bookings[j].status === 'CANCELLED') {
                        cancelCount = cancelCount + 1;
                    }
                }
                allCompleteCount.push(completeCount);
                allCancelCount.push(cancelCount);
            }
        }
        setAllCompleteCount(allCompleteCount);
        setAllCancelCount(allCancelCount);
    }, [bookinglistdata.bookings]);

    return (
        bookinglistdata.loading || usersdata.loading ? <CircularLoading /> :
            <div >
                {/* Section Revenus */}
                <Typography variant="h4" style={{ margin: "20px 20px 0 15px", textAlign: isRTL === 'rtl' ? 'right' : 'left',fontFamily: FONT_FAMILY }}>{t('gross_earning')}</Typography>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12} sm={6} md={3} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.WALLET_CARD,color:colors.WHITE }} title={t('today_text')} image={require("../assets/img/money1.jpg").default}>{settings.symbol + ' ' + formatAmount(dailygross, settings.decimal, settings.country)}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.WALLET_CARD,color:colors.WHITE }} title={t('today_text')} image={require("../assets/img/money1.jpg").default}>{formatAmount(dailygross, settings.decimal, settings.country) + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: '#FF9800',color:colors.WHITE }} title={t('this_week_text') || 'Semaine'} image={require("../assets/img/money2.jpg").default}>{settings.symbol + ' ' + formatAmount(weeklyGross, settings.decimal, settings.country)}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: '#FF9800',color:colors.WHITE }} title={t('this_week_text') || 'Semaine'} image={require("../assets/img/money2.jpg").default}>{formatAmount(weeklyGross, settings.decimal, settings.country) + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.VIOLET,color:colors.WHITE }} title={t('this_month_text')} image={require("../assets/img/money2.jpg").default}>{settings.symbol + ' ' + formatAmount(monthlygross, settings.decimal, settings.country)}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.VIOLET,color:colors.WHITE }} title={t('this_month_text')} image={require("../assets/img/money2.jpg").default}>{formatAmount(monthlygross, settings.decimal, settings.country) + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.GREEN,color:colors.WHITE }} title={t('total')} image={require("../assets/img/money3.jpg").default}>{settings.symbol + ' ' + formatAmount(totalgross, settings.decimal, settings.country)}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.GREEN,color:colors.WHITE }} title={t('total')} image={require("../assets/img/money3.jpg").default}>{formatAmount(totalgross, settings.decimal, settings.country)+ ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                </Grid>

                {/* Section Statut des Réservations en Temps Réel */}
                <Typography variant="h4" style={{ margin: "30px 20px 10px 15px", textAlign: isRTL === 'rtl' ? 'right' : 'left', fontFamily: FONT_FAMILY }}>{t('booking_status') || 'Statut des Réservations'}</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={2}>
                        <Card style={{ borderRadius: "15px", backgroundColor: '#FF5722', padding: 15, textAlign: 'center' }}>
                            <PendingActionsIcon style={{ color: 'white', fontSize: 40 }} />
                            <Typography style={{ color: 'white', fontSize: 14, marginTop: 5, fontFamily: FONT_FAMILY }}>En Attente</Typography>
                            <Typography style={{ color: 'white', fontSize: 28, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{pendingBookings}</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Card style={{ borderRadius: "15px", backgroundColor: '#2196F3', padding: 15, textAlign: 'center' }}>
                            <CheckCircleIcon style={{ color: 'white', fontSize: 40 }} />
                            <Typography style={{ color: 'white', fontSize: 14, marginTop: 5, fontFamily: FONT_FAMILY }}>Acceptées</Typography>
                            <Typography style={{ color: 'white', fontSize: 28, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{acceptedBookings}</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Card style={{ borderRadius: "15px", backgroundColor: '#4CAF50', padding: 15, textAlign: 'center' }}>
                            <LocalTaxiIcon style={{ color: 'white', fontSize: 40 }} />
                            <Typography style={{ color: 'white', fontSize: 14, marginTop: 5, fontFamily: FONT_FAMILY }}>En Cours</Typography>
                            <Typography style={{ color: 'white', fontSize: 28, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{ongoingTrips}</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Card style={{ borderRadius: "15px", backgroundColor: '#8BC34A', padding: 15, textAlign: 'center' }}>
                            <CheckCircleIcon style={{ color: 'white', fontSize: 40 }} />
                            <Typography style={{ color: 'white', fontSize: 14, marginTop: 5, fontFamily: FONT_FAMILY }}>Terminées (Auj.)</Typography>
                            <Typography style={{ color: 'white', fontSize: 28, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{todayCompletedBookings}</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Card style={{ borderRadius: "15px", backgroundColor: '#f44336', padding: 15, textAlign: 'center' }}>
                            <CancelIcon style={{ color: 'white', fontSize: 40 }} />
                            <Typography style={{ color: 'white', fontSize: 14, marginTop: 5, fontFamily: FONT_FAMILY }}>Annulées (Auj.)</Typography>
                            <Typography style={{ color: 'white', fontSize: 28, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{todayCancelledBookings}</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Card style={{ borderRadius: "15px", backgroundColor: '#9C27B0', padding: 15, textAlign: 'center' }}>
                            <TrendingUpIcon style={{ color: 'white', fontSize: 40 }} />
                            <Typography style={{ color: 'white', fontSize: 14, marginTop: 5, fontFamily: FONT_FAMILY }}>Valeur Moy.</Typography>
                            <Typography style={{ color: 'white', fontSize: 22, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>
                                {settings.swipe_symbol === false ? settings.symbol + ' ' + formatAmount(avgTripValue, settings.decimal, settings.country) : formatAmount(avgTripValue, settings.decimal, settings.country) + ' ' + settings.symbol}
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>
                
                {/* Section Carte et Statistiques Utilisateurs */}
                <Grid item xs={12} style={{ marginTop: 20 }}>
                    <Grid container style={{  direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }} >
                        <Grid item xs={12} sm={12} md={9} lg={9} style={{ backgroundColor: colors.WHITE, borderRadius: 20, overflow: 'hidden', }} boxShadow={3}>
                            <Typography variant="h4" style={{ margin: "20px 20px 0 15px", textAlign: isRTL === 'rtl' ? 'right' : 'left',fontFamily: FONT_FAMILY }}>{t('real_time_driver_section_text')}</Typography>
                            {mylocation && mylocation.lat?
                            <GoogleMap
                                zoom={locations.length === 1 ? 12 : locations.length > 1 ? 6 : 10}
                                center={mylocation}
                                mapContainerStyle={{height: `380px`, width: '100%', fontFamily: FONT_FAMILY }}
                                >
                                {locations.map(marker => (
                                    <Marker
                                        position={{ lat: marker.lat, lng: marker.lng }}
                                        key={marker.id}
                                        icon={{
                                            url: marker.carImage,
                                            scaledSize: new window.google.maps.Size(35,25)
                                        }}
                                    >
                                        <InfoWindow 
                                           position={{ lat: marker.lat, lng: marker.lng }} 
                                           options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                        >
                                            <div style={{ 
                                                fontFamily: FONT_FAMILY, 
                                                color: '#000000', 
                                                backgroundColor: '#ffffff',
                                                padding: '8px 12px',
                                                minWidth: '150px',
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#333', marginBottom: '4px' }}>{marker.drivername}</div>
                                                <div style={{ fontSize: '13px', color: '#555', marginBottom: '2px' }}>{marker.carnumber}</div>
                                                <div style={{ fontSize: '12px', color: '#777' }}>{marker.cartype}</div>
                                                {marker.bookingRef && <div style={{ fontSize: '12px', color: '#4CAF50', marginTop: '4px', fontWeight: '500' }}>🚗 Course: {marker.bookingRef}</div>}
                                            </div>
                                        </InfoWindow>
                                    </Marker>
                                ))}
                            </GoogleMap>
                            :null}
                        </Grid>
                        <Grid item xs={12} sm={12} md={3} lg={3} style={{ padding: 25, marginTop: '30px' }}>
                            <Card style={{ borderRadius: "19px", backgroundColor: colors.WALLET_CARD, minHeight: 80, marginBottom: 15, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>
                                <Box>
                                    <Typography style={{ color: 'white', fontSize: 14, fontFamily: FONT_FAMILY }}>{t('total_cumtomer')}</Typography>
                                    <Typography style={{ color: 'white', fontSize: 24, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{customerCount}</Typography>
                                </Box>
                                <DirectionsCarIcon style={{ color: 'rgba(255,255,255,0.3)', fontSize: 50 }} />
                            </Card>
                            <Card style={{ borderRadius: "19px", backgroundColor: colors.VIOLET, minHeight: 80, marginBottom: 15, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>
                                <Box>
                                    <Typography style={{ color: 'white', fontSize: 14, fontFamily: FONT_FAMILY }}>{t('total_drivers')}</Typography>
                                    <Typography style={{ color: 'white', fontSize: 24, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{driverCount}</Typography>
                                </Box>
                                <LocalTaxiIcon style={{ color: 'rgba(255,255,255,0.3)', fontSize: 50 }} />
                            </Card>
                            <Card style={{ borderRadius: "19px", backgroundColor: colors.GREEN, minHeight: 80, marginBottom: 15, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>
                                <Box>
                                    <Typography style={{ color: 'white', fontSize: 14, fontFamily: FONT_FAMILY }}>{t('active_driver')}</Typography>
                                    <Typography style={{ color: 'white', fontSize: 24, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{activeCount}</Typography>
                                </Box>
                                <CheckCircleIcon style={{ color: 'rgba(255,255,255,0.3)', fontSize: 50 }} />
                            </Card>
                            {auth.profile.usertype === 'admin' && (
                                <Card style={{ borderRadius: "19px", backgroundColor: '#607D8B', minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>
                                    <Box>
                                        <Typography style={{ color: 'white', fontSize: 14, fontFamily: FONT_FAMILY }}>{t('fleetadmins') || 'Fleet Admins'}</Typography>
                                        <Typography style={{ color: 'white', fontSize: 24, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{fleetAdminCount}</Typography>
                                    </Box>
                                    <DirectionsCarIcon style={{ color: 'rgba(255,255,255,0.3)', fontSize: 50 }} />
                                </Card>
                            )}
                        </Grid>

                    </Grid>
                </Grid>
                <Box style={{ padding: 10, marginTop: 20, borderRadius: 20, backgroundColor: colors.WHITE}} boxShadow={6}>
                    <Typography style={{margin: "5px 20px 0 15px",color:colors.GREEN, fontWeight:"bold",fontFamily: FONT_FAMILY}} variant="h5">{t('booking_chart')}</Typography>
                    <Chart
                        options={barChartOptions}
                        series={barChart}
                        type="area"
                        width="100%"
                        height={360}
                    />
                </Box>
            </div>

    )
}

export default Dashboard;